import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  motion,
  useMotionTemplate,
  useMotionValue,
  useSpring,
  useTransform
} from 'framer-motion';
import {
  Book,
  MapTrifold,
  GameController,
  Trophy,
  FolderOpen,
  Sparkle,
  CheckCircle
} from 'phosphor-react';
import { useUserStore } from '../store/userStore';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Zizo } from '../components/common/Zizo';
import { RESOURCES } from '../data/resources';

const pageMotion = {
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 12 }
};

const questionTints = [
  'linear-gradient(135deg, rgba(186,230,253,0.58) 0%, rgba(255,255,255,0.8) 100%)',
  'linear-gradient(135deg, rgba(253,224,71,0.4) 0%, rgba(255,255,255,0.82) 100%)',
  'linear-gradient(135deg, rgba(110,231,183,0.42) 0%, rgba(255,255,255,0.82) 100%)',
  'linear-gradient(135deg, rgba(244,114,182,0.28) 0%, rgba(255,255,255,0.82) 100%)'
];

const DAILY_QUESTIONS = [
  'ما أهمية نهر النيل في حياة المصريين؟',
  'اذكر معدنًا تستخدمه في المنزل.',
  'ما الفرق بين الطاقة المتجددة وغير المتجددة؟',
  'لماذا نرشّد استهلاك الماء؟',
  'اذكر مثالًا لطاقة متجددة في مصر.',
  'ما المحصول المشهور في دلتا النيل؟'
];

const HERO_TICKER = [
  '🌍 استكشف مكانًا جديدًا اليوم',
  '🎯 افتح تحديًا سريعًا في الجوائز',
  '🧠 جرّب سؤالًا من اسأل زيزو',
  '🗺️ استخدم فلاتر الخريطة للحصول على نقاط أكثر',
  '🏆 كل مهمة منجزة تقربك من شارة جديدة'
];

const FLOATING_STICKERS = [
  { id: 'float-star', icon: '⭐', top: '14%', right: '16%', shift: 10, rotate: 8, duration: 8.2, delay: 0.2 },
  { id: 'float-map', icon: '🗺️', top: '72%', left: '12%', shift: -8, rotate: -6, duration: 9.4, delay: 0.8 },
  { id: 'float-water', icon: '💧', bottom: '14%', right: '34%', shift: 9, rotate: 12, duration: 7.8, delay: 1.1 },
  { id: 'float-plant', icon: '🌱', top: '32%', left: '8%', shift: -10, rotate: -7, duration: 8.8, delay: 0.4 }
];

const ORBIT_TAGS = [
  {
    id: 'orbit-water',
    label: 'مياه',
    duration: 18,
    inset: 0,
    direction: 1,
    bg: 'linear-gradient(110deg, rgba(56,189,248,0.9), rgba(255,255,255,0.9))'
  },
  {
    id: 'orbit-energy',
    label: 'طاقة',
    duration: 14,
    inset: 16,
    direction: -1,
    bg: 'linear-gradient(110deg, rgba(245,158,11,0.88), rgba(255,255,255,0.9))'
  },
  {
    id: 'orbit-minerals',
    label: 'معادن',
    duration: 20,
    inset: 32,
    direction: 1,
    bg: 'linear-gradient(110deg, rgba(167,139,250,0.9), rgba(255,255,255,0.9))'
  }
];

const decodeArabicMojibake = (value = '') => {
  if (typeof value !== 'string' || !/[ØÙ]/.test(value)) return value;

  try {
    const bytes = Uint8Array.from(Array.from(value).map((char) => char.charCodeAt(0)));
    const decoded = new TextDecoder('utf-8').decode(bytes);
    return /[\u0600-\u06ff]/.test(decoded) ? decoded : value;
  } catch {
    return value;
  }
};

const QuickActionCard = ({ action, index, isAutoActive, onFocus }) => {
  const Icon = action.icon;
  const tiltX = useMotionValue(0);
  const tiltY = useMotionValue(0);
  const glowX = useMotionValue(50);
  const glowY = useMotionValue(50);
  const hoverStrength = useMotionValue(0);

  const rotateX = useSpring(tiltX, { stiffness: 240, damping: 18, mass: 0.25 });
  const rotateY = useSpring(tiltY, { stiffness: 240, damping: 18, mass: 0.25 });
  const liftY = useTransform(hoverStrength, [0, 1], [0, -10]);
  const shadow = useTransform(
    hoverStrength,
    [0, 1],
    ['0 10px 24px rgba(30,41,59,0.08)', '0 24px 44px rgba(56,189,248,0.28)']
  );
  const spotlightOpacity = useTransform(hoverStrength, [0, 1], [0, 1]);
  const spotlight = useMotionTemplate`radial-gradient(220px circle at ${glowX}% ${glowY}%, rgba(255,255,255,0.72), rgba(255,255,255,0) 62%)`;

  const updateTilt = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const px = (event.clientX - rect.left) / rect.width;
    const py = (event.clientY - rect.top) / rect.height;

    tiltY.set((px - 0.5) * 10);
    tiltX.set((0.5 - py) * 10);
    glowX.set(px * 100);
    glowY.set(py * 100);
    hoverStrength.set(1);
    onFocus(index);
  };

  const resetTilt = () => {
    tiltX.set(0);
    tiltY.set(0);
    glowX.set(50);
    glowY.set(50);
    hoverStrength.set(0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 * index, duration: 0.35 }}
      style={{ perspective: 1200 }}
    >
      <Link
        to={action.to}
        onMouseEnter={() => {
          hoverStrength.set(1);
          onFocus(index);
        }}
        onMouseMove={updateTilt}
        onMouseLeave={resetTilt}
        onFocus={() => onFocus(index)}
      >
        <motion.div
          style={{
            rotateX,
            rotateY,
            y: liftY,
            boxShadow: shadow,
            transformStyle: 'preserve-3d'
          }}
          className={`glass-panel p-5 rounded-3xl overflow-hidden relative group transition-all duration-300 ${
            isAutoActive ? 'ring-2 ring-cyan-300/70' : ''
          }`}
        >
          <div className={`absolute inset-0 bg-gradient-to-br ${action.gradient} opacity-45 group-hover:opacity-70 transition duration-300`} />
          <motion.div className="absolute inset-0 pointer-events-none rounded-3xl" style={{ background: spotlight, opacity: spotlightOpacity }} />

          {isAutoActive && (
            <motion.div
              className="absolute inset-0 pointer-events-none"
              animate={{ x: ['-120%', '120%'] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'linear' }}
              style={{
                background: 'linear-gradient(95deg, transparent 0%, rgba(255,255,255,0.45) 50%, transparent 100%)'
              }}
            />
          )}

          <div className="relative flex items-start justify-between gap-4">
            <div className="space-y-1">
              <p className="text-lg font-black text-gray-900">{action.title}</p>
              <p className="text-sm text-gray-700">{action.subtitle}</p>
              {isAutoActive && <span className="chip text-[10px] mt-1">موصى به الآن</span>}
            </div>

            <motion.div
              animate={
                isAutoActive
                  ? { scale: [1, 1.12, 1], rotate: [0, 5, -5, 0] }
                  : { scale: 1, rotate: 0 }
              }
              transition={{ duration: 2, repeat: isAutoActive ? Infinity : 0, ease: 'easeInOut' }}
              className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${action.iconBg} text-white flex items-center justify-center shadow-lg`}
            >
              <Icon size={22} weight="fill" />
            </motion.div>
          </div>
        </motion.div>
      </Link>
    </motion.div>
  );
};

export const Home = () => {
  const { name, points, level, streak, completedLessons, mapInteractions, gamesWon, updateStreak } = useUserStore();

  const [activeAction, setActiveAction] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState({});

  useEffect(() => {
    updateStreak();
  }, [updateStreak]);

  const featured = useMemo(() => {
    const index = Math.abs(points + mapInteractions * 3 + gamesWon * 7) % RESOURCES.length;
    return RESOURCES[index];
  }, [points, mapInteractions, gamesWon]);

  const quickActions = useMemo(
    () => [
      {
        to: '/map',
        title: 'الخريطة التفاعلية',
        subtitle: 'اطّلع على الموارد والخدمات',
        icon: MapTrifold,
        gradient: 'from-cyan-300/80 via-blue-300/70 to-indigo-300/80',
        iconBg: 'from-cyan-500 to-indigo-500'
      },
      {
        to: '/lessons',
        title: 'الدروس',
        subtitle: `مكتمل: ${completedLessons.length}/3`,
        icon: Book,
        gradient: 'from-amber-300/80 via-orange-300/70 to-rose-300/80',
        iconBg: 'from-amber-500 to-rose-500'
      },
      {
        to: '/games',
        title: 'الألعاب',
        subtitle: 'اختبر نفسك بسرعة',
        icon: GameController,
        gradient: 'from-emerald-300/80 via-lime-300/70 to-teal-300/80',
        iconBg: 'from-emerald-500 to-teal-500'
      },
      {
        to: '/library',
        title: 'المكتبة',
        subtitle: 'ملفات ووسائط',
        icon: FolderOpen,
        gradient: 'from-fuchsia-300/80 via-violet-300/70 to-sky-300/80',
        iconBg: 'from-fuchsia-500 to-sky-500'
      },
      {
        to: '/awards',
        title: 'الجوائز',
        subtitle: 'تتبع إنجازاتك',
        icon: Trophy,
        gradient: 'from-yellow-300/80 via-pink-300/70 to-orange-300/80',
        iconBg: 'from-yellow-500 to-pink-500'
      }
    ],
    [completedLessons.length]
  );

  const missions = useMemo(
    () => [
      {
        id: 'mission-map',
        title: 'افتح الخريطة وجرّب فلتر جديد',
        progress: Math.min(mapInteractions, 5),
        target: 5
      },
      {
        id: 'mission-lessons',
        title: 'أكمل درسًا واحدًا',
        progress: Math.min(completedLessons.length, 1),
        target: 1
      },
      {
        id: 'mission-streak',
        title: 'حافظ على سلسلة أيامك',
        progress: Math.min(streak, 7),
        target: 7
      }
    ],
    [mapInteractions, completedLessons.length, streak]
  );

  useEffect(() => {
    const timerId = setInterval(() => {
      setActiveAction((prev) => (prev + 1) % quickActions.length);
    }, 3400);

    return () => clearInterval(timerId);
  }, [quickActions.length]);

  const nextLevelIn = 50 - (points % 50);
  const answeredCount = Object.values(answeredQuestions).filter(Boolean).length;
  const tickerLoop = useMemo(() => [...HERO_TICKER, ...HERO_TICKER], []);
  const normalizedName = String(name || '').trim();
  const isGuestUser = !normalizedName || normalizedName === 'زائر';
  const greetingLineOne = isGuestUser ? 'أهلًا بك في كنوز مصر.' : `أهلًا يا ${normalizedName}.`;
  const greetingLineTwo = isGuestUser ? 'ابدأ رحلتك واكتشف كنوز بلدك خطوة بخطوة.' : 'خلّينا نفتح كنوز مصر معًا.';

  const featuredName = decodeArabicMojibake(featured.name);
  const featuredDescription = decodeArabicMojibake(featured.description);
  const featuredGovernorate = decodeArabicMojibake(featured.governorate);

  return (
    <motion.div
      {...pageMotion}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      className="container mx-auto px-4 py-10 space-y-10"
    >
      <section className="glass-panel relative overflow-hidden p-7 md:p-10">
        <div className="absolute -top-28 -right-24 w-72 h-72 rounded-full bg-cyan-300/35 blur-3xl" style={{ animation: 'floatOrb 12s ease-in-out infinite' }} />
        <div className="absolute -bottom-28 -left-24 w-80 h-80 rounded-full bg-pink-300/28 blur-3xl" style={{ animation: 'floatOrb 11s ease-in-out infinite reverse' }} />

        {FLOATING_STICKERS.map((item) => (
          <motion.div
            key={item.id}
            className="absolute text-2xl md:text-3xl pointer-events-none select-none"
            style={{ top: item.top, right: item.right, left: item.left, bottom: item.bottom }}
            animate={{
              y: [0, -10, 0],
              x: [0, item.shift, 0],
              rotate: [0, item.rotate, 0]
            }}
            transition={{
              duration: item.duration,
              repeat: Infinity,
              delay: item.delay,
              ease: 'easeInOut'
            }}
          >
            {item.icon}
          </motion.div>
        ))}

        <div className="relative grid grid-cols-1 md:grid-cols-[1fr_auto] gap-8 items-center">
          <div className="space-y-5 md:space-y-6">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="chip chip-strong">
                <Sparkle size={16} weight="fill" />
                لوحة التحكم
              </span>
              <span className="chip">اليوم: {new Date().toLocaleDateString('ar-EG')}</span>
            </div>

            <h1 className="text-4xl md:text-5xl font-black tracking-tight text-gray-900 leading-[1.2] md:leading-[1.22]">
              <span className="block">{greetingLineOne}</span>
              <span className="block mt-2 md:mt-3">{greetingLineTwo}</span>
            </h1>

            <p className="text-gray-700 max-w-2xl leading-8 md:leading-9">
              الصفحة الرئيسية أصبحت أكثر حركة وتفاعل: اختصارات ذكية متحركة، عناصر حية، وأسئلة يومية تتفاعل معها مباشرة.
            </p>

            <div className="flex flex-wrap gap-3">
              <Link to="/map">
                <Button size="large">ابدأ الاستكشاف</Button>
              </Link>
              <Link to="/lessons">
                <Button size="large" variant="secondary">
                  واصل التعلم
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
              <div className="chip justify-between">
                <span className="text-slate-600">النقاط</span>
                <span className="number font-black">{points}</span>
              </div>
              <div className="chip justify-between">
                <span className="text-slate-600">المستوى</span>
                <span className="number font-black">{level}</span>
              </div>
              <div className="chip justify-between">
                <span className="text-slate-600">السلسلة</span>
                <span className="number font-black">{streak}</span>
              </div>
              <div className="chip justify-between">
                <span className="text-slate-600">التالي</span>
                <span className="number font-black">{nextLevelIn} نقطة</span>
              </div>
            </div>
          </div>

          <div className="relative justify-self-center w-[250px] h-[250px] flex items-center justify-center">
            <motion.div
              className="absolute rounded-full border border-cyan-200/70"
              style={{ inset: 16 }}
              animate={{ scale: [1, 1.08, 1], opacity: [0.4, 0.18, 0.4] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
              className="absolute rounded-full border border-pink-200/65"
              style={{ inset: 32 }}
              animate={{ scale: [1, 1.06, 1], opacity: [0.3, 0.15, 0.3] }}
              transition={{ duration: 4.2, repeat: Infinity, ease: 'easeInOut' }}
            />

            {ORBIT_TAGS.map((tag) => (
              <motion.div
                key={tag.id}
                className="absolute rounded-full border border-white/50"
                style={{ inset: tag.inset }}
                animate={{ rotate: tag.direction * 360 }}
                transition={{ duration: tag.duration, repeat: Infinity, ease: 'linear' }}
              >
                <span
                  className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[10px] font-black text-slate-900 shadow-sm"
                  style={{ background: tag.bg }}
                >
                  {tag.label}
                </span>
              </motion.div>
            ))}

            <motion.div
              animate={{ y: [0, -6, 0], rotate: [0, 2, -2, 0] }}
              transition={{ duration: 4.8, repeat: Infinity, ease: 'easeInOut' }}
              className="relative"
            >
              <Zizo size="large" expression="wave" />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="glass-panel p-3 overflow-hidden">
        <motion.div
          className="flex items-center gap-2 w-max"
          animate={{ x: ['0%', '-50%'] }}
          transition={{ duration: 26, repeat: Infinity, ease: 'linear' }}
        >
          {tickerLoop.map((item, index) => (
            <span key={`${item}-${index}`} className="chip whitespace-nowrap">
              {item}
            </span>
          ))}
        </motion.div>
      </section>

      <section className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-2xl font-black text-gray-900">ابدأ بسرعة</h2>
          <span className="chip">اختصارات ذكية</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {quickActions.map((action, index) => (
            <QuickActionCard
              key={action.to}
              action={action}
              index={index}
              isAutoActive={activeAction === index}
              onFocus={setActiveAction}
            />
          ))}
        </div>

        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="chip chip-strong">المسار المقترح الآن: {quickActions[activeAction]?.title}</span>
          <div className="flex items-center gap-2">
            {quickActions.map((action, index) => (
              <button
                key={`dot-${action.to}`}
                type="button"
                onClick={() => setActiveAction(index)}
                className={`w-2.5 h-2.5 rounded-full transition ${
                  activeAction === index ? 'bg-cyan-500 scale-125' : 'bg-slate-300'
                }`}
                aria-label={`تفعيل ${action.title}`}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-black text-gray-900">مهمات اليوم</h3>
            <span className="chip">تكسب نقاط أسرع</span>
          </div>

          <div className="space-y-3">
            {missions.map((mission, index) => {
              const percent = Math.round((mission.progress / mission.target) * 100);
              return (
                <div key={mission.id} className="rounded-2xl border border-white/70 bg-white/70 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-semibold text-gray-900">{mission.title}</p>
                    <span className="chip">{mission.progress}/{mission.target}</span>
                  </div>

                  <div className="mt-3 h-2 rounded-full bg-gray-200 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${percent}%` }}
                      transition={{ duration: 0.8, ease: 'easeOut' }}
                      className="h-2 rounded-full relative overflow-hidden"
                      style={{
                        background: 'linear-gradient(90deg, #38BDF8 0%, #FB7185 55%, #F59E0B 120%)'
                      }}
                    >
                      <motion.span
                        className="absolute inset-y-0 -left-12 w-12 bg-white/60 blur-[1px]"
                        animate={{ x: [0, 260] }}
                        transition={{
                          duration: 2.6,
                          repeat: Infinity,
                          ease: 'linear',
                          delay: index * 0.35
                        }}
                      />
                    </motion.div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-black text-gray-900">اقتراح للاستكشاف</h3>
            <span className="chip">موقع مميز</span>
          </div>

          <motion.div
            className="rounded-3xl overflow-hidden border border-white/40 bg-white/60"
            animate={{ y: [0, -4, 0] }}
            transition={{ duration: 5.4, repeat: Infinity, ease: 'easeInOut' }}
          >
            <div className="p-5 space-y-2">
              <p className="text-lg font-black text-gray-900">{featuredName}</p>
              <p className="text-sm text-gray-700">{featuredDescription}</p>
              <div className="flex flex-wrap gap-2 pt-2">
                <span className="chip">📍 {featuredGovernorate}</span>
                <span className="chip">⭐ {'★'.repeat(featured.importance)}</span>
              </div>
            </div>
            <div className="px-5 pb-5">
              <Link to="/map">
                <Button className="w-full">افتحه على الخريطة</Button>
              </Link>
            </div>
          </motion.div>
        </Card>
      </section>

      <section className="max-w-5xl mx-auto">
        <Card className="space-y-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className="text-2xl font-black text-gray-900">سؤال اليوم</h3>
            <div className="flex items-center gap-2">
              <span className="chip">تم التفاعل {answeredCount}/{DAILY_QUESTIONS.length}</span>
              <button type="button" className="chip" onClick={() => setAnsweredQuestions({})}>
                إعادة
              </button>
            </div>
          </div>

          <p className="text-gray-700">
            اضغط على أي سؤال بعد التفكير فيه علشان تعلّمه كمجاب. الأسئلة نفسها بتتحرك بشكل حي لتجربة أكثر تفاعلًا.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {DAILY_QUESTIONS.map((question, index) => {
              const isAnswered = Boolean(answeredQuestions[question]);
              return (
                <motion.button
                  key={question}
                  type="button"
                  className={`rounded-3xl border border-white/70 px-4 py-3 text-sm font-semibold text-gray-900 text-right transition ${
                    isAnswered ? 'ring-2 ring-emerald-300/70' : ''
                  }`}
                  style={{
                    background: isAnswered
                      ? 'linear-gradient(135deg, rgba(167,243,208,0.55) 0%, rgba(255,255,255,0.84) 100%)'
                      : questionTints[index % questionTints.length]
                  }}
                  animate={{ y: [0, -3, 0] }}
                  transition={{ duration: 4.5, repeat: Infinity, delay: index * 0.25, ease: 'easeInOut' }}
                  whileHover={{ scale: 1.02, y: -4 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() =>
                    setAnsweredQuestions((prev) => ({
                      ...prev,
                      [question]: !prev[question]
                    }))
                  }
                >
                  <div className="flex items-start justify-between gap-3">
                    <span>{question}</span>
                    <span
                      className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isAnswered ? 'bg-emerald-500 text-white' : 'bg-white/80 text-slate-500'
                      }`}
                    >
                      {isAnswered ? <CheckCircle size={18} weight="fill" /> : '؟'}
                    </span>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </Card>
      </section>
    </motion.div>
  );
};
